/**
 * Draws a v2 multi-track timeline into one canvas.
 *
 * Shared by the program monitor and export, so a scrubbed frame and the file
 * are the same pixels. Scene clips reuse the Fabric scene renderer. Video
 * clips are seeked, never played. Text and captions are canvas type.
 */

import type {
  ProjectPage,
  TextClipStyle,
  TimelineClip,
  VideoProject,
  VideoScene,
} from '@canvas/shared-types';
import { isHardCut } from '@/data/transitions';
import { ease } from './easing';
import { abortError, isAbortError } from './exportErrors';
import type { FrameCompositor } from './frameCompositor';
import type { Size } from './frameGeometry';
import { createSceneRenderer, type SceneRenderer } from './sceneCompositor';
import {
  boxFromPlacement,
  captionStyle,
  layoutText,
  mediaContentSize,
  resolvedPlacement,
  sourcePixels,
  type TextMeasure,
} from './clipFrame';
import { clipBlendsAt, isClipProject, type ClipBlend } from './timelineEdit';
import { TRANSITION_RENDERERS } from './transitions';
import { clipSourceMsAt } from './videoClip';
import { createVideoFrameSource, type VideoFrameSource } from './videoFrameSource';
import { resolveMediaBlob } from './videoAssetUpload';
import { decodeGifStrip, gifFrameIndex, isGifMime, type GifStrip } from './gifFrames';
import { REEL_CAPTION_KEY } from '@/lib/reel/reelCaption';

const MISSING_MEDIA = 'A clip’s media couldn’t be loaded, so that moment is empty.';

function make2d(canvas: HTMLCanvasElement, alpha: boolean): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { alpha });
  if (!ctx) throw new Error('This browser can’t draw video frames (no 2D canvas).');
  return ctx;
}

function scratch(out: Size): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = out.width;
  canvas.height = out.height;
  return canvas;
}

function clear(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
}

function sceneMeta(clip: TimelineClip): VideoScene {
  return {
    v: 1,
    pageId: clip.scene?.pageId || '',
    durationMs: clip.durationMs,
    transitionIn: { id: 'cut', durationMs: 0 },
  };
}

function textMotion(style: TextClipStyle, localMs: number, durationMs: number): { opacity: number; dy: number; scale: number } {
  const edge = 400;
  let opacity = 1;
  let dy = 0;
  let scale = 1;
  if (style.inPreset && style.inPreset !== 'none' && localMs < edge) {
    const p = ease('ease-out', localMs / edge);
    if (style.inPreset === 'fade-in') opacity = p;
    if (style.inPreset === 'rise') {
      opacity = p;
      dy = (1 - p) * 48;
    }
    if (style.inPreset === 'pop-in') {
      opacity = Math.min(1, p * 1.2);
      scale = 0.82 + 0.18 * p;
    }
  }
  const outStart = Math.max(0, durationMs - edge);
  if (style.outPreset && style.outPreset !== 'none' && localMs > outStart) {
    const p = ease('ease-in', (localMs - outStart) / edge);
    if (style.outPreset === 'fade-out') opacity *= 1 - p;
    if (style.outPreset === 'sink') {
      opacity *= 1 - p;
      dy += p * 48;
    }
    if (style.outPreset === 'pop-out') {
      opacity *= 1 - p;
      scale *= 1 - 0.15 * p;
    }
  }
  return { opacity, dy, scale };
}

function fillRound(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
  ctx.fill();
}

function measureOf(ctx: CanvasRenderingContext2D): TextMeasure {
  return (text, font) => {
    ctx.font = font;
    return ctx.measureText(text || ' ').width;
  };
}

function paintLayout(
  ctx: CanvasRenderingContext2D,
  layout: ReturnType<typeof layoutText>,
  motion: { opacity: number; dy: number; scale: number },
  frameHeight: number
): void {
  const { box } = layout;
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, motion.opacity));
  ctx.translate(cx, cy + motion.dy * (frameHeight / 1080));
  ctx.rotate((box.rotation * Math.PI) / 180);
  ctx.scale((box.flipX ? -1 : 1) * motion.scale, (box.flipY ? -1 : 1) * motion.scale);
  if (layout.look === 'circular') {
    const radius = Math.min(box.width, box.height) / 2;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fillStyle = layout.background || '#3b82f6';
    ctx.fill();
    ctx.lineWidth = Math.max(4, layout.fontPx * 0.16);
    ctx.strokeStyle = '#1d4ed8';
    ctx.stroke();
  } else if (layout.look === 'button' && layout.background) {
    const radius = Math.min(box.height / 2, layout.fontPx);
    ctx.fillStyle = layout.background;
    fillRound(ctx, -box.width / 2, -box.height / 2, box.width, box.height, radius);
  } else if (layout.bar && layout.background) {
    ctx.fillStyle = layout.background;
    ctx.fillRect(-box.width / 2, -box.height / 2, box.width, box.height);
  }
  ctx.font = layout.font;
  ctx.textAlign = layout.align;
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  const inner = box.width - (layout.bar || layout.look === 'button' ? layout.fontPx * 0.9 : 0);
  const anchor = layout.align === 'left' ? -inner / 2 : layout.align === 'right' ? inner / 2 : 0;
  const block = (layout.lines.length - 1) * layout.lineHeight;
  const drawLine = (line: string, x: number, y: number, fill: string): void => {
    ctx.fillStyle = fill;
    ctx.fillText(line, x, y);
  };
  layout.lines.forEach((line, index) => {
    const y = -block / 2 + index * layout.lineHeight;
    const shift = layout.fontPx;
    if (layout.look === 'pride') {
      drawLine(line, anchor - shift * 0.06, y, '#5aa2ff');
      drawLine(line, anchor + shift * 0.06, y, '#ff5b7a');
      drawLine(line, anchor, y + shift * 0.045, '#ffe14a');
    }
    if (layout.look === 'retro') {
      drawLine(line, anchor + shift * 0.1, y + shift * 0.1, '#3d8bfd');
      drawLine(line, anchor + shift * 0.065, y + shift * 0.065, '#ff4f9a');
      drawLine(line, anchor + shift * 0.03, y + shift * 0.03, '#ffd23a');
    }
    if (layout.look === 'creator' || layout.look === 'bubble') {
      ctx.strokeStyle = layout.look === 'bubble' ? '#16300a' : '#1a1a1a';
      ctx.lineWidth = layout.fontPx * (layout.look === 'bubble' ? 0.14 : 0.16);
      ctx.strokeText(line, anchor, y);
    }
    drawLine(line, anchor, y, layout.fill);
    if (layout.caret && index === layout.lines.length - 1) {
      const width = ctx.measureText(line).width;
      const left = layout.align === 'center' ? anchor - width / 2 : layout.align === 'right' ? anchor - width : anchor;
      ctx.fillStyle = layout.fill;
      ctx.fillRect(left + width + 3, y - layout.fontPx * 0.45, Math.max(2, layout.fontPx * 0.06), layout.fontPx * 0.9);
    }
  });
  ctx.restore();
}

function paintText(
  ctx: CanvasRenderingContext2D,
  clip: TimelineClip,
  localMs: number,
  out: Size
): void {
  const style = clip.text;
  if (!style) return;
  if (style.fullFrame) {
    const gradient = ctx.createLinearGradient(0, 0, out.width, out.height);
    gradient.addColorStop(0, style.background || '#241433');
    gradient.addColorStop(1, style.gradientTo || style.background || '#5b3a8c');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, out.width, out.height);
  }
  if (!style.text.trim()) return;
  const layout = layoutText(style, clip.placement, out, measureOf(ctx));
  if (style.look === 'typewriter') {
    const count = Math.max(0, Math.min(style.text.length, Math.floor(localMs / 70)));
    layout.lines = [style.text.slice(0, count)];
    layout.caret = count < style.text.length && Math.floor(localMs / 450) % 2 === 0;
  }
  paintLayout(ctx, layout, textMotion(style, localMs, clip.durationMs), out.height);
}

function paintCaption(ctx: CanvasRenderingContext2D, clip: TimelineClip, localMs: number, out: Size): void {
  const style = captionStyle(clip, localMs);
  if (!style?.text.trim()) return;
  const layout = layoutText(style, clip.placement, out, measureOf(ctx));
  paintLayout(ctx, layout, { opacity: 1, dy: 0, scale: 1 }, out.height);
}

function paintPlaced(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  pixelWidth: number,
  pixelHeight: number,
  clip: TimelineClip,
  out: Size
): void {
  const content = mediaContentSize(clip);
  const placement = resolvedPlacement(clip, out, content);
  const box = boxFromPlacement(placement, out, content);
  const src = sourcePixels(clip.image?.crop, pixelWidth, pixelHeight);
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((box.rotation * Math.PI) / 180);
  ctx.scale(box.flipX ? -1 : 1, box.flipY ? -1 : 1);
  ctx.drawImage(source, src.x, src.y, src.width, src.height, -box.width / 2, -box.height / 2, box.width, box.height);
  ctx.restore();
}

/** Composite every visible clip at `ms`. */
export async function createClipFrameCompositor(args: {
  pages: ProjectPage[];
  video: VideoProject;
  target: HTMLCanvasElement;
  signal?: AbortSignal;
}): Promise<FrameCompositor> {
  const { target, signal } = args;
  let project = args.video;
  if (!isClipProject(project)) throw new Error('Clip compositor requires a multi-track project.');
  const out: Size = { width: target.width, height: target.height };
  const outCtx = make2d(target, false);
  const aCanvas = scratch(out);
  const bCanvas = scratch(out);
  const aCtx = make2d(aCanvas, true);
  const bCtx = make2d(bCanvas, true);
  const pagesById = new Map(args.pages.map((page) => [page.pageId, page]));
  const scenes = new Map<string, Promise<SceneRenderer>>();
  const videos = new Map<string, Promise<VideoFrameSource>>();
  const images = new Map<string, Promise<ImageBitmap | GifStrip | null>>();
  const warnings = new Set<string>();
  let disposed = false;

  const sceneFor = (clip: TimelineClip): Promise<SceneRenderer> => {
    const pageId = clip.scene?.pageId || '';
    let pending = scenes.get(pageId);
    if (!pending) {
      const page = pagesById.get(pageId);
      if (!page) {
        pending = Promise.reject(new Error('missing page'));
      } else {
        pending = createSceneRenderer({ page, scene: sceneMeta(clip), out, signal });
      }
      scenes.set(pageId, pending);
      pending.catch(() => {
        if (scenes.get(pageId) === pending) scenes.delete(pageId);
      });
    }
    return pending;
  };

  const videoFor = (clip: TimelineClip): Promise<VideoFrameSource> => {
    let pending = videos.get(clip.id);
    if (!pending) {
      if (!clip.video) pending = Promise.reject(new Error('missing video'));
      else pending = createVideoFrameSource({ clip: clip.video, signal });
      videos.set(clip.id, pending);
      pending.catch(() => {
        if (videos.get(clip.id) === pending) videos.delete(clip.id);
      });
    }
    return pending;
  };

  const imageFor = (clip: TimelineClip): Promise<ImageBitmap | GifStrip | null> => {
    let pending = images.get(clip.id);
    if (!pending) {
      pending = (async () => {
        if (!clip.image) return null;
        const blob = await resolveMediaBlob(clip.image);
        if (!blob) return null;
        if (isGifMime(clip.image.mimeType) || isGifMime(blob.type)) {
          const strip = await decodeGifStrip(blob);
          if (strip) return strip;
        }
        return await createImageBitmap(blob);
      })();
      images.set(clip.id, pending);
    }
    return pending;
  };

  const paintClip = async (ctx: CanvasRenderingContext2D, clip: TimelineClip, localMs: number): Promise<void> => {
    if (clip.kind === 'scene' && clip.scene) {
      try {
        const renderer = await sceneFor(clip);
        const el = await renderer.renderAt(Math.max(0, Math.min(localMs, clip.durationMs)));
        const fit = renderer.fit;
        ctx.drawImage(el, fit.x, fit.y, fit.width, fit.height);
      } catch (err) {
        if (isAbortError(err)) throw err;
        warnings.add('A scene clip points at a page that could not be drawn.');
      }
      return;
    }
    if (clip.kind === 'video' && clip.video) {
      try {
        const source = await videoFor(clip);
        await source.seekTo(clipSourceMsAt(clip.video, localMs));
        const element = source.element;
        const pixelWidth = element.videoWidth || clip.video.naturalWidth || out.width;
        const pixelHeight = element.videoHeight || clip.video.naturalHeight || out.height;
        paintPlaced(ctx, element, pixelWidth, pixelHeight, clip, out);
      } catch (err) {
        if (isAbortError(err)) throw err;
        warnings.add(MISSING_MEDIA);
      }
      return;
    }
    if (clip.kind === 'image' && clip.image) {
      try {
        const bitmap = await imageFor(clip);
        if (!bitmap) {
          warnings.add(MISSING_MEDIA);
          return;
        }
        if ('frames' in bitmap) {
          const index = gifFrameIndex(bitmap.endsMs, bitmap.totalMs, localMs);
          paintPlaced(ctx, bitmap.frames[index], bitmap.width || clip.image.width, bitmap.height || clip.image.height, clip, out);
          return;
        }
        const pixelWidth = bitmap.width || clip.image.width;
        const pixelHeight = bitmap.height || clip.image.height;
        paintPlaced(ctx, bitmap, pixelWidth, pixelHeight, clip, out);
      } catch (err) {
        if (isAbortError(err)) throw err;
        warnings.add(MISSING_MEDIA);
      }
      return;
    }
    if (clip.kind === 'text' && clip.text) {
      paintText(ctx, clip, localMs, out);
      return;
    }
    if (clip.kind === 'caption') paintCaption(ctx, clip, localMs, out);
  };

  const paintBlend = async (ctx: CanvasRenderingContext2D, blend: ClipBlend, ms: number): Promise<void> => {
    clear(aCtx, aCanvas);
    clear(bCtx, bCanvas);
    await paintClip(aCtx, blend.outgoing, ms - blend.outgoing.startMs);
    await paintClip(bCtx, blend.incoming, ms - blend.incoming.startMs);
    const transition = blend.incoming.transitionIn;
    if (!transition || isHardCut(transition.id)) {
      ctx.drawImage(bCanvas, 0, 0);
      return;
    }
    const p = ease(transition.easing ?? 'ease-in-out', Math.min(1, Math.max(0, blend.progress)));
    ctx.save();
    TRANSITION_RENDERERS[transition.id](ctx, aCanvas, bCanvas, p, {
      width: out.width,
      height: out.height,
      direction: transition.direction,
      color: transition.color,
    });
    ctx.restore();
  };

  const frameCanvas = scratch(out);
  const frameCtx = make2d(frameCanvas, false);
  const pageStamps = new Map<string, string>();
  let renderSerial = 0;
  let chain: Promise<void> = Promise.resolve();

  const pageStamp = (page: ProjectPage): string => {
    const objects = page.canvasData?.objects || [];
    let caption = '';
    for (const obj of objects) {
      const record = obj as Record<string, unknown>;
      if (record[REEL_CAPTION_KEY] === true && typeof record.text === 'string') caption = record.text;
    }
    return `${objects.length}:${caption}:${String(page.canvasData?.background ?? '')}`;
  };

  const dropScene = (pageId: string): void => {
    const pending = scenes.get(pageId);
    if (!pending) return;
    scenes.delete(pageId);
    void pending.then((renderer) => renderer.dispose()).catch(() => undefined);
  };

  for (const page of args.pages) pageStamps.set(page.pageId, pageStamp(page));

  const paintFrame = async (ms: number, serial: number): Promise<void> => {
    const current = (): boolean => !disposed && serial === renderSerial;
    if (!current()) return;
    if (signal?.aborted) throw abortError();
    const video = project;
    if (!isClipProject(video)) return;
    frameCtx.setTransform(1, 0, 0, 1, 0, 0);
    frameCtx.globalAlpha = 1;
    frameCtx.fillStyle = video.canvas.bg || '#111111';
    frameCtx.fillRect(0, 0, out.width, out.height);
    const tracks = video.tracks
      .filter((track) => track.kind !== 'audio' && !track.hidden)
      .slice()
      .sort((a, b) => a.index - b.index);
    for (const track of tracks) {
      if (!current()) return;
      const onTrack = video.clips
        .filter((clip) => clip.trackId === track.id)
        .sort((a, b) => (a.z ?? 0) - (b.z ?? 0) || a.startMs - b.startMs);
      const blends = clipBlendsAt(onTrack, ms);
      const consumed = new Set(blends.flatMap((blend) => [blend.outgoing.id, blend.incoming.id]));
      for (const clip of onTrack) {
        if (!current()) return;
        if (consumed.has(clip.id) || ms < clip.startMs || ms >= clip.startMs + clip.durationMs) continue;
        if (signal?.aborted) throw abortError();
        await paintClip(frameCtx, clip, ms - clip.startMs);
      }
      for (const blend of blends) {
        if (!current()) return;
        if (signal?.aborted) throw abortError();
        await paintBlend(frameCtx, blend, ms);
      }
    }
    if (!current()) return;
    outCtx.setTransform(1, 0, 0, 1, 0, 0);
    outCtx.globalAlpha = 1;
    outCtx.drawImage(frameCanvas, 0, 0);
  };

  return {
    get durationMs(): number {
      return project.totalDurationMs;
    },

    replaceProject(next: VideoProject): void {
      if (next.v === 2 && next.clips && next.tracks && next.canvas) project = next;
    },

    setPages(next: ProjectPage[]): void {
      const seen = new Set<string>();
      for (const page of next) {
        seen.add(page.pageId);
        const mark = pageStamp(page);
        if (pageStamps.get(page.pageId) === mark && pagesById.has(page.pageId)) continue;
        pageStamps.set(page.pageId, mark);
        pagesById.set(page.pageId, page);
        dropScene(page.pageId);
      }
      for (const pageId of [...scenes.keys()]) {
        if (seen.has(pageId)) continue;
        dropScene(pageId);
        pageStamps.delete(pageId);
      }
    },

    async renderFrameAt(ms: number): Promise<void> {
      if (disposed) throw new Error('Compositor used after dispose');
      if (signal?.aborted) throw abortError();
      const serial = ++renderSerial;
      const job = chain.then(() => paintFrame(ms, serial));
      chain = job.then(() => undefined, () => undefined);
      return job;
    },

    warnings(): string[] {
      return Array.from(warnings);
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      renderSerial += 1;
      for (const pending of scenes.values()) {
        void pending.then((renderer) => renderer.dispose()).catch(() => undefined);
      }
      for (const pending of videos.values()) {
        void pending.then((source) => source.dispose()).catch(() => undefined);
      }
      for (const pending of images.values()) {
        void pending.then((image) => {
          if (!image) return;
          if ('frames' in image) image.frames.forEach((frame) => frame.close());
          else image.close();
        }).catch(() => undefined);
      }
      scenes.clear();
      videos.clear();
      images.clear();
      aCanvas.width = 0;
      bCanvas.width = 0;
      frameCanvas.width = 0;
    },
  };
}
